/**
 * variant-doomscroll.js
 * Companion script for sections/variant-doomscroll.liquid
 *
 * - Seeded shuffle (mulberry32) so the "random" order is fixed per seed, not
 *   re-randomized on every request (avoids CDN cache thrash + matches "same
 *   random set until I choose to reshuffle" behavior).
 * - Renders cards via Shopify's Section Rendering API against
 *   `variant-doomscroll-card`, a lightweight single-image card section.
 * - Lazy-loads in batches via IntersectionObserver until total_items is reached.
 */
(function () {
  function mulberry32(seed) {
    return function () {
      seed |= 0;
      seed = (seed + 0x6d2b79f5) | 0;
      let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  function seededShuffle(array, rand) {
    const result = array.slice();
    for (let i = result.length - 1; i > 0; i--) {
      const j = Math.floor(rand() * (i + 1));
      [result[i], result[j]] = [result[j], result[i]];
    }
    return result;
  }

  /**
   * De-cluster variants by product. A plain shuffle still, by chance, drops
   * several variants of the SAME product next to each other. This spreads each
   * product's variants evenly across the whole feed:
   *  - never places two cards of the same product back-to-back (unless one
   *    product literally has more than half of everything left), and
   *  - weights each pick by how many variants that product has remaining, so a
   *    product with many variants is paced out across the feed instead of
   *    clumping at the start or end.
   */
  function spreadByProduct(items, rand) {
    const buckets = new Map();
    for (const item of items) {
      if (!buckets.has(item.handle)) buckets.set(item.handle, []);
      buckets.get(item.handle).push(item);
    }
    let groups = Array.from(buckets.values());
    const result = [];
    let lastHandle = null;

    while (groups.length) {
      let candidates = groups.filter((g) => g[0].handle !== lastHandle);
      if (candidates.length === 0) candidates = groups; // only one product left
      const total = candidates.reduce((sum, g) => sum + g.length, 0);
      let r = rand() * total;
      let pick = candidates[0];
      for (const g of candidates) {
        r -= g.length;
        if (r <= 0) {
          pick = g;
          break;
        }
      }
      const item = pick.shift();
      result.push(item);
      lastHandle = item.handle;
      if (pick.length === 0) groups = groups.filter((g) => g.length);
    }
    return result;
  }

  class VariantDoomscroll {
    constructor(root) {
      this.root = root;
      this.grid = root.querySelector('[data-grid]');
      this.sentinel = root.querySelector('[data-sentinel]');
      this.status = root.querySelector('[data-status]');
      this.totalItems = parseInt(root.dataset.totalItems, 10) || 360;
      this.batchSize = parseInt(root.dataset.batchSize, 10) || 20;
      this.seed = parseInt(root.dataset.shuffleSeed, 10) || 1;
      this.cursor = 0;
      this.loading = false;

      let pool = [];
      const poolEl = root.querySelector('[data-variant-pool]');
      try {
        pool = JSON.parse((poolEl.textContent || '[]').trim() || '[]');
      } catch (e) {
        console.error('variant-doomscroll: could not parse variant pool JSON', e);
      }

      const rand = mulberry32(this.seed);
      const available = pool.filter((p) => p.available !== false);
      this.items = spreadByProduct(seededShuffle(available, rand), rand).slice(
        0,
        this.totalItems
      );

      if (this.items.length === 0) {
        this.status.hidden = false;
        this.status.textContent = 'No variants found for this collection.';
        return;
      }

      this.observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) this.loadNextBatch();
      });
      this.observer.observe(this.sentinel);

      this.loadNextBatch();
    }

    async loadNextBatch() {
      if (this.loading || this.cursor >= this.items.length) {
        if (this.cursor >= this.items.length) {
          this.observer && this.observer.disconnect();
          this.status.hidden = true;
        }
        return;
      }

      this.loading = true;
      this.status.hidden = false;
      this.status.textContent = 'Loading more…';

      const batch = this.items.slice(this.cursor, this.cursor + this.batchSize);
      this.cursor += batch.length;

      const htmlResponses = await Promise.all(
        batch.map((item) =>
          fetch(
            `/products/${item.handle}?variant=${item.variant_id}&section_id=variant-doomscroll-card`
          )
            .then((res) => (res.ok ? res.text() : ''))
            .catch(() => '')
        )
      );

      const frag = document.createDocumentFragment();
      htmlResponses.forEach((html) => {
        if (!html) return;
        const wrapper = document.createElement('div');
        wrapper.innerHTML = html.trim();
        if (wrapper.firstElementChild) frag.appendChild(wrapper.firstElementChild);
      });
      this.grid.appendChild(frag);

      this.loading = false;
      this.status.hidden = this.cursor >= this.items.length;
    }
  }

  document.querySelectorAll('.variant-doomscroll').forEach((root) => new VariantDoomscroll(root));
})();
